import React, { useCallback, useEffect, useState } from 'react';
import { Route, useHistory, useRouteMatch } from 'react-router-dom';
import { Currency, Hours, IconLink, Page, Percent } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useAuth } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import {
  BigValue,
  Card,
  DashboardGrid,
  Divider,
  Eyebrow,
  Legend,
  LegendLabel,
  LegendRow,
  LegendShare,
  LegendValue,
  shareLabel,
  Unit,
  ValueRow,
} from '../DashboardCards';
import EditMonthlyBudgetDrawer from './EditMonthlyBudgetDrawer';

export default function MonthlyBudgetDashboard({ project }) {
  const { url, path } = useRouteMatch();
  const auth = useAuth();
  const history = useHistory();
  const api = useApi();
  const { workspace } = useWorkspace();
  const [budget, setBudget] = useState(null);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).monthlyBudgetDashboard();
    setBudget(data);
  }, [api, workspace.id, project]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!budget) return <PageLoader />;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Monthly Budget</Page.Title>

        {auth.projects.sections.budget && (
          <Page.Actions>
            <IconLink icon="pencil" tooltip="Edit Monthly Budget" to={`${url}/edit`} />
          </Page.Actions>
        )}
      </Page.TabHeader>

      {/* The read-only tab is a dashboard of the saved monthly budget; the per-entity ledgers live in the drawer. */}
      <MonthlyBudgetKpiCards budget={budget} isBillable={project.isBillable} />

      {auth.projects.sections.budget && (
        <Route path={`${path}/edit`}>
          <EditMonthlyBudgetDrawer projectId={project.id} onSaved={fetchData} onClose={() => history.push(url)} />
        </Route>
      )}
    </>
  );
}

function MonthlyBudgetKpiCards({ budget, isBillable }) {
  const currency = budget.currency;
  const money = (value) => <Currency value={value} currency={currency} />;

  const revenueSources = [
    { label: 'Monthly Budget Services Revenue', value: budget.servicesRevenue },
    { label: 'Monthly Budget Expenses Revenue', value: budget.expensesRevenue },
    { label: 'Monthly Budget Products Revenue', value: budget.productRevenue },
    {
      label: 'Monthly Budget Other Items Revenue',
      value: budget.otherItemsRevenue,
    },
  ];

  // A non-billable project has no revenue, so the revenue-shaped cards come off; hours still mean something.
  return (
    <DashboardGrid>
      {isBillable && (
        <Card>
          <Eyebrow>Monthly Budget Revenue</Eyebrow>
          <BigValue>{money(budget.totalRevenue)}</BigValue>
          <Divider />
          <Legend>
            {revenueSources.map((source) => (
              <LegendRow key={source.label}>
                <LegendLabel>{source.label}</LegendLabel>
                <LegendValue>{money(source.value)}</LegendValue>
                <LegendShare>{shareLabel(source.value, budget.totalRevenue)}</LegendShare>
              </LegendRow>
            ))}
          </Legend>
        </Card>
      )}

      <Card>
        <Eyebrow>Monthly Budget Hours</Eyebrow>
        <BigValue>
          <Hours value={budget.totalHours} />
        </BigValue>
        <Divider />
        <Legend>
          {isBillable && (
            <LegendRow>
              <LegendLabel>Monthly Budget Billable Hours</LegendLabel>
              <LegendValue>
                <Hours value={budget.billableHours} />
              </LegendValue>
              <LegendShare>{shareLabel(budget.billableHours, budget.totalHours)}</LegendShare>
            </LegendRow>
          )}
          <LegendRow>
            <LegendLabel>Monthly Budget Non-Billable Hours</LegendLabel>
            <LegendValue>
              <Hours value={budget.nonBillableHours} />
            </LegendValue>
            <LegendShare>{shareLabel(budget.nonBillableHours, budget.totalHours)}</LegendShare>
          </LegendRow>
        </Legend>
      </Card>

      {isBillable && (
        <Card>
          <Eyebrow>Monthly Budget Effective Bill Rate</Eyebrow>
          <ValueRow>
            <BigValue $flush>{money(budget.effectiveBillRate)}</BigValue>
            <Unit>/ hr</Unit>
          </ValueRow>
          <Divider />
          <Legend>
            <LegendRow>
              <LegendLabel>Monthly Budget Services Revenue</LegendLabel>
              <LegendValue>{money(budget.servicesRevenue)}</LegendValue>
            </LegendRow>
            <LegendRow>
              <LegendLabel>Monthly Budget Total Hours</LegendLabel>
              <LegendValue>
                <Hours value={budget.totalHours} />
              </LegendValue>
            </LegendRow>
          </Legend>
        </Card>
      )}

      {isBillable && (
        <Card>
          <Eyebrow>Monthly Budget Realization Rate</Eyebrow>
          <BigValue>
            <Percent value={budget.realizationRate} />
          </BigValue>
          <Divider />
          <Legend>
            <LegendRow>
              <LegendLabel>Monthly Budget Billable Hours</LegendLabel>
              <LegendValue>
                <Hours value={budget.billableHours} />
              </LegendValue>
            </LegendRow>
            <LegendRow>
              <LegendLabel>Monthly Budget Total Hours</LegendLabel>
              <LegendValue>
                <Hours value={budget.totalHours} />
              </LegendValue>
            </LegendRow>
          </Legend>
        </Card>
      )}
    </DashboardGrid>
  );
}
